/** Monthly recurrence options for an assigned post-it. */
import type {
  MonthlyOptions as MonthlyOptionsType,
  MonthNumber,
  PostItColor,
} from '@/types/post-it.types';
import { cn } from '@/lib/utils';
import {
  TEXT_COLOR_MAP,
  BORDER_COLOR_MAP,
  POST_IT_MONTH_OPTIONS,
} from '@/components/post-its/post-it-configs';
import {
  getScheduleChipClasses,
  isMonthActive,
  toggleAllMonths,
  toggleTargetMonth,
} from '@/components/post-its/post-it-form-utils';

/** Props for {@link MonthlyOptions}. */
interface MonthlyOptionsProps {
  value: MonthlyOptionsType;
  onChange: (value: MonthlyOptionsType) => void;
  color?: PostItColor;
}

/** Monthly recurrence options for an assigned post-it. */
export function MonthlyOptions({
  value,
  onChange,
  color = 'jaune',
}: Readonly<MonthlyOptionsProps>) {
  const textColor = TEXT_COLOR_MAP[color] || TEXT_COLOR_MAP.jaune;
  const borderColor = BORDER_COLOR_MAP[color] || BORDER_COLOR_MAP.jaune;

  const handleMonthToggle = (month: MonthNumber) => {
    onChange({ ...value, targetMonths: toggleTargetMonth(value.targetMonths, month) });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <p className={cn('text-xs font-medium', textColor)}>Mois ciblés</p>
          <button
            type="button"
            onClick={() =>
              onChange({ ...value, targetMonths: toggleAllMonths(value.targetMonths) })
            }
            className={cn(
              'text-xs px-2 py-0.5 rounded border transition-all',
              borderColor,
              getScheduleChipClasses(color, value.targetMonths === 'all'),
            )}
          >
            Tous
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {POST_IT_MONTH_OPTIONS.map((m) => {
            const active = isMonthActive(value.targetMonths, m.value as MonthNumber);
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => handleMonthToggle(m.value as MonthNumber)}
                className={cn(
                  'w-9 py-1 rounded text-xs border transition-all',
                  borderColor,
                  getScheduleChipClasses(color, active),
                )}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
